import { useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { Btn } from "@/components/Btn";
import { api } from "@/lib/api";
import { CITIES } from "@/lib/cities";

export default function LeadForm({ compact = false, defaultCity = "" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: defaultCity, details: "", preferred_contact: "", heard_choice: "", heard_text: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const CONTACT_OPTS = [["Text", "Text me"], ["Call", "Call me"], ["Email", "Email me"]];
  const prefs = form.preferred_contact ? form.preferred_contact.split(", ") : [];
  const togglePref = (v) => setForm((f) => {
    const cur = f.preferred_contact ? f.preferred_contact.split(", ") : [];
    const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
    return { ...f, preferred_contact: CONTACT_OPTS.map(([o]) => o).filter((o) => next.includes(o)).join(", ") };
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please add your name, email and phone!");
      return;
    }
    setBusy(true);
    try {
      const photo_paths = [];
      for (const file of files.slice(0, 6)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data?.path) photo_paths.push(data.path);
      }
      const { heard_choice, heard_text, ...rest } = form;
      const heard_from = [heard_choice, heard_text.trim()].filter(Boolean).join(": ");
      await api.post("/leads", { ...rest, heard_from, photo_paths });
      if (window.gtag) window.gtag("event", "generate_lead", { method: "form" });
      if (window.fbq) window.fbq("track", "Lead");
      toast.success("Got it! We will get right back to you. One call and it skedaddles!");
      setForm({ name: "", email: "", phone: "", address: "", city: defaultCity, details: "", preferred_contact: "", heard_choice: "", heard_text: "" });
      setFiles([]);
    } catch (err) {
      toast.error("Something hiccuped. Please call or text us at 843-518-0021!");
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full bg-white border-2 border-charcoal rounded-2xl px-4 py-3 font-body text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:ring-4 focus:ring-sunny/50";

  const HEARD = ["Google search", "Google Maps", "Facebook", "Instagram", "TikTok", "Nextdoor", "Saw our truck or a sign", "Friend or family", "Realtor or other business", "Used us before", "Other"];
  const heardPh = {
    "Friend or family": "Who sent you our way? (optional)",
    "Realtor or other business": "Which one? (optional)",
    "Facebook": "Which page or group? (optional)",
    "Nextdoor": "Anything to add? (optional)",
    "Other": "Where did you find us?",
  }[form.heard_choice] || "Anything to add? (optional)";

  return (
    <form onSubmit={submit} className="grid gap-3" data-testid="lead-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <input className={input} value={form.name} onChange={set("name")} placeholder="Name*" type="text" autoComplete="name" data-testid="lead-name" />
        <input className={input} value={form.phone} onChange={set("phone")} placeholder="Phone*" type="tel" inputMode="tel" autoComplete="tel" data-testid="lead-phone" />
      </div>
      <input className={input} value={form.email} onChange={set("email")} placeholder="Email*" type="email" inputMode="email" autoComplete="email" data-testid="lead-email" />
      <div className="grid gap-3 sm:grid-cols-2">
        <select className={input} value={form.city} onChange={set("city")} data-testid="lead-city">
          <option value="">City (optional)</option>
          {CITIES.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
        </select>
        <input className={input} value={form.address} onChange={set("address")} placeholder="Address (optional)" type="text" autoComplete="street-address" data-testid="lead-address" />
      </div>
      <textarea className={`${input} ${compact ? "min-h-20" : "min-h-24"}`} value={form.details} onChange={set("details")} placeholder="What needs to go?" data-testid="lead-details" />
      <div data-testid="lead-contact-pref">
        <div className="font-body text-sm text-charcoal/70 mb-2">Best way to reach you? <span className="text-charcoal/50">(pick any)</span></div>
        <div className="flex flex-wrap gap-2">
          {CONTACT_OPTS.map(([v, l]) => {
            const on = prefs.includes(v);
            return (
              <button key={v} type="button" onClick={() => togglePref(v)} aria-pressed={on} data-testid={`lead-pref-${v.toLowerCase()}`}
                className={`font-body font-semibold text-sm rounded-full px-4 py-2.5 border-2 border-charcoal transition-colors ${on ? "bg-teal text-cream" : "bg-white text-charcoal hover:bg-sunny/30"}`}>
                {on ? "✓ " : ""}{l}
              </button>
            );
          })}
        </div>
      </div>
      <select className={`${input} ${form.heard_choice ? "text-charcoal" : "text-charcoal/40"}`} value={form.heard_choice} onChange={set("heard_choice")} aria-label="How did you hear about us?" data-testid="lead-heard">
        <option value="">How did you hear about us? (optional)</option>
        {HEARD.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
      {form.heard_choice && (
        <input className={input} value={form.heard_text} onChange={set("heard_text")} placeholder={heardPh} type="text" maxLength={120} data-testid="lead-heard-text" />
      )}
      <label className="flex items-center gap-3 bg-white border-2 border-dashed border-charcoal rounded-2xl px-4 py-3 cursor-pointer hover:bg-sunny/20" data-testid="lead-photo-label">
        <Upload className="w-5 h-5" />
        <span className="font-body text-charcoal/70 text-sm">{files.length ? `${files.length} photo(s)` : "Add photos (recommended!)"}</span>
        <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setFiles([...e.target.files])} data-testid="lead-photo-input" />
      </label>
      <Btn type="submit" variant="primary" size={compact ? "md" : "lg"} className="w-full" disabled={busy} data-testid="lead-submit">
        {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending...</> : "Get My Free Quote"}
      </Btn>
      <p className="font-body text-charcoal/50 text-xs text-center">No spam, ever. We only use your info to send your quote.</p>
    </form>
  );
}
